import React, { useState, useCallback, createContext, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const NavigationContext = createContext(null);

export const NavigationProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [viewStack, setViewStack] = useState([]);
  
  // Track route changes
  useEffect(() => {
    queueMicrotask(() => {
      setHistory(prev => {
        const newHistory = [...prev, location.pathname];
        return newHistory.slice(-20);
      });
      setViewStack([]);
    });
  }, [location.pathname]);
  
  // Push a view onto the stack (for modals, panels, etc.)
  const pushView = useCallback((viewId, title) => {
    setViewStack(prev => [...prev, { id: viewId, title }]);
  }, []);
  
  // Pop the current view
  const popView = useCallback(() => {
    setViewStack(prev => {
      if (prev.length > 0) {
        return prev.slice(0, -1);
      }
      return prev;
    });
  }, []);
  
  // Clear all views
  const clearViews = useCallback(() => {
    setViewStack([]);
  }, []);
  
  // Go back - either pop view or navigate back
  const goBack = useCallback(() => {
    if (viewStack.length > 0) {
      popView();
      return true;
    }
    
    if (history.length > 1) {
      navigate(-1);
      return true;
    }
    
    // Default to home
    navigate('/');
    return true;
  }, [viewStack, history, navigate, popView]);
  
  // Check if we can go back
  const canGoBack = viewStack.length > 0 || history.length > 1;
  
  // Get current view title
  const currentView = viewStack.length > 0 ? viewStack[viewStack.length - 1] : null;
  
  // Get depth (how many views deep we are)
  const depth = viewStack.length;
  
  return (
    <NavigationContext.Provider value={{
      history,
      viewStack,
      pushView,
      popView,
      clearViews,
      goBack,
      canGoBack,
      currentView,
      depth,
    }}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigationHistory = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigationHistory must be used within NavigationProvider');
  }
  return context;
};

export default useNavigationHistory;
